import { IAddress, isUS } from 'src/types/address';
import { IFieldErrors } from 'src/types/validation';
import { countryOptions, usStateOptions } from 'src/constants';
import { styled } from '@mui/system';
import { Grid } from '@mui/material';
import { Typography } from 'src/components';
import { Input, Dropdown } from 'src/generic/component/form';
import { FieldNote, FieldLabel } from 'src/generic/component';
import { useState } from 'react';
import { get } from 'lodash';
import { Draft } from 'src/types/common';
import { ChangeManager } from 'src/generic/utils/form';

const FieldRow = styled(Grid)`
  margin-bottom: 10px;
`;

interface AddressFieldProps {
  readOnly?: boolean;
  address?: Draft<IAddress>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IAddress>;
  showDiff?: boolean;
  onUpdate?: (address: Draft<IAddress>) => void;
  additionalInfo?: boolean;
  customLabels?: any;
  addressNote?: string;
}

export const AddressFields = ({
  additionalInfo,
  customLabels,
  readOnly,
  errors,
  address,
  original,
  showDiff,
  addressNote = '(P.O. Box addresses are not acceptable)',
  onUpdate,
}: AddressFieldProps) => {
  const [streetAddr1, setAddress] = useState<string | undefined>(address?.streetAddr1);
  const [streetAddr2, setAddress2] = useState<string | undefined>(address?.streetAddr2);
  const [streetAddr3, setAddress3] = useState<string | undefined>(address?.streetAddr3);
  const [countryIsoCd, setCountry] = useState<string>(address?.countryIsoCd || 'US');
  const [city, setCity] = useState<string | undefined>(address?.city);

  const [stateCd, setState] = useState<string | undefined>(address?.stateCd);
  const [zip5, setZip5] = useState<string | undefined>(address?.zip5);
  const [zip4, setZip4] = useState<string | undefined>(address?.zip4);

  const [province, setProvince] = useState<string | undefined>(address?.province);
  const [intlPostalCd, setIntlPostalCd] = useState<string | undefined>(address?.intlPostalCd);

  const [label, setLabel] = useState<string | undefined>(address?.label);
  const [contactName, setContactName] = useState<string | undefined>(address?.contactName);
  const [contactPhone, setContactPhone] = useState<string | undefined>(address?.contactPhone);

  const getData = (): Draft<IAddress> => {
    const base = {
      streetAddr1,
      streetAddr2,
      streetAddr3,
      countryIsoCd,
      city,
      label,
      contactName,
      contactPhone,
    };

    if (isUS(countryIsoCd)) {
      return {
        ...base,
        stateCd,
        zip5,
        zip4,
      };
    } else {
      return {
        ...base,
        province,
        intlPostalCd,
      };
    }
  };

  if (showDiff && !original) throw new Error('Must send the original value when comparing differences.');
  const { getCurrent, isChanged, getChangeColor } = ChangeManager<IAddress>(original || {}, address || {});

  const changed = (updates: Draft<IAddress>) => {
    if (onUpdate) {
      const current = getData();
      onUpdate({ ...current, ...updates });
    }
  };

  const getErrors = (field: string): string[] => {
    const error = get(errors, `${field}._errors`, []);
    return Array.isArray(error) ? error : [];
  };

  const hasErrors = (field: string): boolean => {
    return getErrors(field).length > 0 ? true : false;
  };

  return (
    <>
      <FieldRow>
        <Typography variant="body1">
          Address <FieldNote>{addressNote}</FieldNote>
        </Typography>

        {showDiff && isChanged('streetAddr1') && (
          <div>
            <i className={getChangeColor('streetAddr1')}>Current: {getCurrent('streetAddr1') || '(empty)'}</i>
          </div>
        )}
        <Input
          disabled={readOnly}
          className={showDiff ? getChangeColor('streetAddr1') : ''}
          validation={hasErrors('streetAddr1') ? 'error' : undefined}
          validationMsg={getErrors('streetAddr1').join('\n')}
          onChange={(e) => {
            const newVal = e.target.value || undefined;
            setAddress(newVal);
            changed({ streetAddr1: newVal });
          }}
          value={streetAddr1 || ''}
        />
      </FieldRow>
      <FieldRow>
        <Typography variant="body1">Address 2</Typography>
        {showDiff && isChanged('streetAddr2') && (
          <div>
            <i className={getChangeColor('streetAddr2')}>Current: {getCurrent('streetAddr2') || '(empty)'}</i>
          </div>
        )}
        <Input
          disabled={readOnly}
          className={showDiff ? getChangeColor('streetAddr2') : ''}
          validation={hasErrors('streetAddr2') ? 'error' : undefined}
          validationMsg={getErrors('streetAddr2').join('\n')}
          onChange={(e) => {
            const newVal = e.target.value || undefined;
            setAddress2(newVal);
            changed({ streetAddr2: newVal });
          }}
          value={streetAddr2 || ''}
        />
      </FieldRow>
      <FieldRow>
        <Typography variant="body1">Address 3</Typography>
        {showDiff && isChanged('streetAddr3') && (
          <div>
            <i className={getChangeColor('streetAddr3')}>Current: {getCurrent('streetAddr3') || '(empty)'}</i>
          </div>
        )}
        <Input
          disabled={readOnly}
          className={showDiff ? getChangeColor('streetAddr3') : ''}
          validation={hasErrors('streetAddr3') ? 'error' : undefined}
          validationMsg={getErrors('streetAddr3').join('\n')}
          onChange={(e) => {
            const newVal = e.target.value || undefined;
            setAddress3(newVal);
            changed({ streetAddr3: newVal });
          }}
          value={streetAddr3 || ''}
        />
      </FieldRow>

      <FieldRow>
        <Typography variant="body1">Country</Typography>
        {showDiff && isChanged('countryIsoCd') && (
          <div>
            <i className={getChangeColor('countryIsoCd')}>Current: {getCurrent('countryIsoCd') || '(empty)'}</i>
          </div>
        )}
        <Dropdown
          values={countryOptions.map((option) => ({ key: option.value, label: option.label }))}
          disabled={readOnly}
          className={showDiff ? getChangeColor('countryIsoCd') : ''}
          validation={hasErrors('countryIsoCd') ? 'error' : undefined}
          validationMsg={getErrors('countryIsoCd').join('\n')}
          onChange={(e) => {
            setCountry(e.target.value);
            changed({ countryIsoCd: e.target.value });
          }}
          value={countryIsoCd}
        />
      </FieldRow>

      <FieldRow>
        <Typography variant="body1">City</Typography>
        {showDiff && isChanged('city') && (
          <div>
            <i className={getChangeColor('city')}>Current: {getCurrent('city') || '(empty)'}</i>
          </div>
        )}
        <Input
          disabled={readOnly}
          className={showDiff ? getChangeColor('city') : ''}
          validation={hasErrors('city') ? 'error' : undefined}
          validationMsg={getErrors('city').join('\n')}
          onChange={(e) => {
            const newVal = e.target.value || undefined;
            setCity(newVal);
            changed({ city: newVal });
          }}
          value={city || ''}
        />
      </FieldRow>
      {isUS(countryIsoCd) ? (
        <div>
          <FieldRow>
            <Typography variant="body1">State</Typography>
            {showDiff && isChanged('stateCd') && (
              <div>
                <i className={getChangeColor('stateCd')}>Current: {getCurrent('stateCd') || '(empty)'}</i>
              </div>
            )}
            <Dropdown
              disabled={readOnly}
              values={usStateOptions.map((option) => ({ key: option.value, label: option.label }))}
              className={showDiff ? getChangeColor('stateCd') : ''}
              validation={hasErrors('stateCd') ? 'error' : undefined}
              validationMsg={getErrors('stateCd').join('\n')}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                setState(newVal);
                changed({ stateCd: newVal });
              }}
              value={stateCd || ''}
            />
          </FieldRow>
          <FieldRow container>
            <Grid item xs={6} style={{ paddingRight: '5px' }}>
              <Typography variant="body1">Zip (5 Digit)</Typography>
              {showDiff && isChanged('zip5') && (
                <div>
                  <i className={getChangeColor('zip5')}>Current: {getCurrent('zip5') || '(empty)'}</i>
                </div>
              )}
              <Input
                disabled={readOnly}
                className={showDiff ? getChangeColor('zip5') : ''}
                validation={hasErrors('zip5') ? 'error' : undefined}
                validationMsg={getErrors('zip5').join('\n')}
                onChange={(e) => {
                  const newVal = e.target.value || undefined;
                  setZip5(newVal);
                  changed({ zip5: newVal });
                }}
                value={zip5 || ''}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1">Zip (4 Digit)</Typography>
              {showDiff && isChanged('zip4') && (
                <div>
                  <i className={getChangeColor('zip4')}>Current: {getCurrent('zip4') || '(empty)'}</i>
                </div>
              )}
              <Input
                disabled={readOnly}
                className={showDiff ? getChangeColor('zip4') : ''}
                validation={hasErrors('zip4') ? 'error' : undefined}
                validationMsg={getErrors('zip4').join('\n')}
                onChange={(e) => {
                  const newVal = e.target.value || undefined;
                  setZip4(newVal);
                  changed({ zip4: newVal });
                }}
                value={zip4 || ''}
              />
            </Grid>
          </FieldRow>
        </div>
      ) : (
        <div>
          <FieldRow>
            <Typography variant="body1">Province</Typography>
            {showDiff && isChanged('province') && (
              <div>
                <i className={getChangeColor('province')}>Current: {getCurrent('province') || '(empty)'}</i>
              </div>
            )}
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('province') : ''}
              validation={hasErrors('province') ? 'error' : undefined}
              validationMsg={getErrors('province').join('\n')}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                setProvince(newVal);
                changed({ province: newVal });
              }}
              value={province || ''}
            />
          </FieldRow>
          <FieldRow>
            <Typography variant="body1">Postal Code</Typography>
            {showDiff && isChanged('intlPostalCd') && (
              <div>
                <i className={getChangeColor('intlPostalCd')}>Current: {getCurrent('intlPostalCd') || '(empty)'}</i>
              </div>
            )}
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('intlPostalCd') : ''}
              validation={hasErrors('intlPostalCd') ? 'error' : undefined}
              validationMsg={getErrors('intlPostalCd').join('\n')}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                setIntlPostalCd(newVal);
                changed({ intlPostalCd: newVal });
              }}
              value={intlPostalCd || ''}
            />
          </FieldRow>
        </div>
      )}
      {additionalInfo && (
        <>
          <FieldRow>
            <FieldLabel>{customLabels?.label || 'Label'}</FieldLabel>
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('label') : ''}
              validation={hasErrors('label') ? 'error' : undefined}
              validationMsg={getErrors('label').join('\n')}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                setLabel(newVal);
                changed({ label: newVal });
              }}
              value={label || ''}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel>{customLabels?.contactName || 'Contact Name'}</FieldLabel>
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('contactName') : ''}
              validation={hasErrors('contactName') ? 'error' : undefined}
              validationMsg={getErrors('contactName').join('\n')}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                setContactName(newVal);
                changed({ contactName: newVal });
              }}
              value={contactName || ''}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel>{customLabels?.contactPhone || 'Contact Phone'}</FieldLabel>
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('contactPhone') : ''}
              validation={hasErrors('contactPhone') ? 'error' : undefined}
              validationMsg={getErrors('contactPhone').join('\n')}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                setContactPhone(newVal);
                changed({ contactPhone: newVal });
              }}
              value={contactPhone || ''}
            />
          </FieldRow>
        </>
      )}
    </>
  );
};
